import React, { useCallback } from 'react';
import { Upload, FileText, AlertCircle } from 'lucide-react';
import { ExcavationSite } from '../types';
import { CSV_HEADER_MAPPING } from '../constants';

interface FileUploadProps {
  onDataLoaded: (data: ExcavationSite[]) => void;
}

export const FileUpload: React.FC<FileUploadProps> = ({ onDataLoaded }) => {
  
  const parseCSV = (text: string): ExcavationSite[] => {
    const lines = text.split('\n').filter(l => l.trim() !== '');
    if (lines.length < 2) return [];

    const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
    const dataRows = lines.slice(1);

    const getIndex = (keys: string[]) => headers.findIndex(h => keys.some(k => h.includes(k)));

    const nameIdx = getIndex(CSV_HEADER_MAPPING.name);
    const locIdx = getIndex(CSV_HEADER_MAPPING.location);
    const perIdx = getIndex(CSV_HEADER_MAPPING.period);
    const descIdx = getIndex(CSV_HEADER_MAPPING.description);
    const latIdx = getIndex(CSV_HEADER_MAPPING.lat);
    const lngIdx = getIndex(CSV_HEADER_MAPPING.lng);

    return dataRows.map((row, idx) => {
      // Basic CSV splitting (doesn't handle commas inside quotes, simple implementation)
      const cols = row.split(',').map(c => c.trim());
      
      return {
        id: `row-${idx}`,
        name: nameIdx !== -1 ? cols[nameIdx] : `Alan ${idx + 1}`,
        location: locIdx !== -1 ? cols[locIdx] : 'Bilinmiyor',
        period: perIdx !== -1 ? cols[perIdx] : 'Belirsiz',
        description: descIdx !== -1 ? cols[descIdx] : '',
        lat: latIdx !== -1 ? (parseFloat(cols[latIdx]) || 0) : 0,
        lng: lngIdx !== -1 ? (parseFloat(cols[lngIdx]) || 0) : 0
      };
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      if (file.name.endsWith('.csv')) {
        const parsed = parseCSV(text);
        onDataLoaded(parsed);
      } else if (file.name.endsWith('.json')) {
        try {
          const parsed = JSON.parse(text) as ExcavationSite[];
          // Basic validation
          if (Array.isArray(parsed)) {
            onDataLoaded(parsed);
          }
        } catch (err) {
          console.error("JSON Parse error", err);
          alert("Geçersiz JSON formatı.");
        }
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="w-full p-8 border-2 border-dashed border-stone-300 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors cursor-pointer text-center group relative">
      <input 
        type="file" 
        accept=".csv,.json" 
        onChange={handleFileChange} 
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
      />
      <div className="flex flex-col items-center justify-center space-y-4">
        <div className="p-4 bg-amber-100 rounded-full group-hover:bg-amber-200 transition-colors">
          <Upload className="w-8 h-8 text-amber-700" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-stone-700">Veri Dosyasını Yükle</h3>
          <p className="text-sm text-stone-500 mt-1">.CSV veya .JSON formatında kazı verisi</p>
        </div>
        <div className="flex items-center text-xs text-stone-400 bg-stone-100 px-3 py-1 rounded-full">
          <FileText className="w-3 h-3 mr-1.5" />
          Örnek: Ad, Konum, Dönem
        </div>
      </div>
    </div>
  );
};